import {
  getPoolUrl,
  type PoolParams,
  useDeleteQuery,
  useGetQuery,
  usePatchQuery,
  usePostQuery,
  useRouteParams
} from "@quortex/front-common"
import type { PaginationParams, RecorderParams } from "@/queries/types"

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------
/**
 * @returns /pools/${poolUuid}/l2v/recorders/${recorderUuid}?org=${orgUuid}
 */
export const getRecorderUrl = ({ orgUuid, poolUuid, recorderUuid }: Required<RecorderParams>) => {
  const params = new URLSearchParams({ org: orgUuid })
  return `/pools/${poolUuid}/l2v/recorders/${recorderUuid}/?${params}`
}

/**
 * @returns /pools/${poolUuid}/l2v/recorders/?org=${orgUuid}
 */
export const getRecorderListUrl = ({
  orgUuid,
  poolUuid
}: Required<PoolParams> & PaginationParams) => {
  const params = new URLSearchParams({ org: orgUuid })
  return `/pools/${poolUuid}/l2v/recorders/?${params}`
}

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const useRecorder = (params?: RecorderParams) => {
  const { orgUuid, poolUuid, recorderUuid } = useRouteParams(params)
  const url = getRecorderUrl({ orgUuid, poolUuid, recorderUuid })

  return useGetQuery<api.ott.L2VRecorder>("ott", url)
}

export const useRecorderList = (params?: PoolParams & PaginationParams) => {
  const { orgUuid, poolUuid } = useRouteParams(params)
  const url = getRecorderListUrl({ orgUuid, poolUuid })

  return useGetQuery<api.ott.PaginatedL2VRecorderGetList>("ott", url)
}

export const useAddRecorder = (params?: PoolParams) => {
  const { orgUuid, poolUuid } = useRouteParams(params)
  const poolUrl = getPoolUrl({ orgUuid, poolUuid })
  const recorderListUrl = getRecorderListUrl({ orgUuid, poolUuid })

  type TRequest = DeepPartial<api.ott.L2VRecorderRequest>
  type TResponse = api.ott.L2VRecorder

  return usePostQuery<TRequest, TResponse>("ott", recorderListUrl, [poolUrl, recorderListUrl])
}

export const useUpdateRecorder = (params?: RecorderParams) => {
  const mergedParams = useRouteParams(params)
  const url = getRecorderUrl(mergedParams)
  const listUrl = getRecorderListUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.L2VRecorderRequest>
  type TResponse = api.ott.L2VRecorder

  return usePatchQuery<TRequest, TResponse>("ott", url, [url, listUrl])
}

export const useDeleteRecorder = (params?: RecorderParams) => {
  const mergedParams = useRouteParams(params)
  const url = getRecorderUrl(mergedParams)
  const listUrl = getRecorderListUrl(mergedParams)

  return useDeleteQuery("ott", url, [url, listUrl])
}
